/**
 * @file src/offline/offlineKeys.js
 * @description The logical identity of everything a download stores, in one place.
 *
 * The audio bucket's key scheme (`audioAssetSource.js`) was always more than a URL: it names the
 * track, the release version, the variant and the format, which is exactly what "the same bytes"
 * means when the same file is served from a signed URL one moment and a proxy the next. A download
 * needs the same property for the things around the audio — a waveform, a cover, a world texture —
 * so they are keyed the same way, in the same bucket, under one scheme.
 *
 * ## Namespaces
 *
 * Every key here begins with a namespace segment. Track assets and world textures describe a
 * PUBLISHED RELEASE. Collection covers, the catalogue, and sky faces have no trustworthy
 * publication version in the current backend contract, so their keys retain URL identity and their
 * reads remain network-first. A user's own file is not a release: it has no platform
 * id and no version, and there is no honest value to put in those segments. It gets its own
 * namespace — `local:` — rather than a fabricated track id, and the store below is written to hold
 * both because a faked id is impossible to take back once bytes are filed under it.
 *
 * Nothing here reaches for a store. These are names; who holds the bytes is somebody else's job.
 */

const CACHE_URL_PREFIX = '/__orbiters/audio-cache__';

/**
 * Namespaces in use. `LOCAL` is declared and unused on purpose — see the file comment; it is the
 * reservation, and a reservation nobody can see is not one.
 */
export const KEY_NAMESPACE = {
  TRACK_AUDIO: 'track',
  WAVEFORM: 'waveform',
  COVER: 'cover',
  TEXTURE: 'texture',
  SKYBOX: 'skybox',
  CATALOGUE: 'catalogue',
  /** Reserved for a user's own files. Nothing writes it yet. */
  LOCAL: 'local',
};

function normalize(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

/**
 * Wrap a logical identity into the same-origin request URL the bucket is actually keyed by.
 * Cache Storage keys on requests, so a logical name has to become one; this is the same transform
 * `audioAssetSource.js` applies, kept identical so a track pinned by a download and the same track
 * cached by playing it are ONE entry, never two.
 * @param {string|null} identityKey
 * @returns {string|null}
 */
export function toCacheRequestUrl(identityKey) {
  const identity = normalize(identityKey);
  if (!identity) return null;
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const url = new URL(CACHE_URL_PREFIX, origin);
    url.searchParams.set('key', identity);
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * The audio key — byte-identical to `buildLogicalCacheIdentity` in `audioAssetSource.js`, which is
 * the whole point: a download must pin the entry playback already writes, not a parallel one.
 *
 * `assetFormat` defaults to null rather than 'mp3' so it can be DERIVED from the variant. The two
 * always travel together in practice (a compressed release is mp3, a lossless one pcm), and a
 * default that quietly stated one of them would name lossless bytes as mp3 for any caller that gave
 * only the variant.
 * @param {{ trackId: string, version: string|number, variant?: 'compressed'|'lossless', assetFormat?: 'mp3'|'pcm'|null }} params
 * @returns {string|null}
 */
export function trackAudioKey({ trackId, version, variant = 'compressed', assetFormat = null }) {
  const id = normalize(trackId);
  const release = normalize(version);
  const chosenVariant = normalize(variant);
  if (!id || !release || !chosenVariant) return null;
  const format = normalize(assetFormat) || (chosenVariant === 'lossless' ? 'pcm' : 'mp3');
  return `${KEY_NAMESPACE.TRACK_AUDIO}:${id}:version:${release}:variant:${chosenVariant}:format:${format}`;
}

/** The track's waveform JSON, pinned to the same release version as its audio. */
export function waveformKey({ trackId, version }) {
  const id = normalize(trackId);
  const release = normalize(version);
  if (!id || !release) return null;
  return `${KEY_NAMESPACE.WAVEFORM}:track:${id}:version:${release}`;
}

/**
 * A collection cover. Collections deliberately have no release version, so its URL is the only
 * identity available; it must remain network-first online.
 */
export function collectionCoverKey(url) {
  const resolved = normalize(url);
  if (!resolved) return null;
  return `${KEY_NAMESPACE.COVER}:collection:${resolved}`;
}

/** Cover art for a versioned published entity. */
export function entityCoverKey({ entityType, entityId, version }) {
  const kind = normalize(entityType)?.toLowerCase();
  const id = normalize(entityId);
  const release = normalize(version);
  if (!['track', 'orbiter', 'world'].includes(kind) || !id || !release) return null;
  return `${KEY_NAMESPACE.COVER}:${kind}:${id}:version:${release}`;
}

/** The track's cover art, at whatever tier the release named. */
export function coverKey({ trackId, version }) {
  return entityCoverKey({ entityType: 'track', entityId: trackId, version });
}

/**
 * A world texture, pinned to the world release that named it. This is deliberately not URL-only:
 * publishing a new world release may replace bytes at the same remote URL.
 */
export function textureKey({ worldId, version, url }) {
  const id = normalize(worldId);
  const release = normalize(version);
  const resolved = normalize(url);
  if (!id || !release || !resolved) return null;
  return `${KEY_NAMESPACE.TEXTURE}:world:${id}:version:${release}:${resolved}`;
}

/**
 * The platform's texture catalogue, keyed by URL and always network-first online.
 *
 * Not a release and not a file on the herbarium, which is why it has a namespace of its own. It is
 * the document that says WHICH sky is default and where the cloud and moon files live, so a download
 * that stores every texture and not this one stores files nothing will ask for: offline the loader
 * falls back to a copy hard-coded in the shared package, and that copy is free to name a different
 * default. The backend's `version: "1.0"` is a schema version, not a publication identity.
 */
export function catalogueKey(url) {
  const resolved = normalize(url);
  if (!resolved) return null;
  return `${KEY_NAMESPACE.CATALOGUE}:${resolved}`;
}

/** One face of a skybox cubemap. No trustworthy publication version exists for these bytes. */
export function skyboxFaceKey({ skyboxId, face, url }) {
  const id = normalize(skyboxId);
  const which = normalize(face);
  const resolved = normalize(url);
  if (!id || !which || !resolved) return null;
  return `${KEY_NAMESPACE.SKYBOX}:${id}:face:${which}:${resolved}`;
}

/** The namespace a key belongs to, or null for anything that is not one of ours. */
export function keyNamespace(identityKey) {
  const identity = normalize(identityKey);
  if (!identity) return null;
  const namespace = identity.split(':', 1)[0];
  return Object.values(KEY_NAMESPACE).includes(namespace) ? namespace : null;
}
